import { X } from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';

import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { REJECTION_REASONS } from '../constants';

interface RejectPopoverProps {
  onReject: (reason: string) => void;
  isRecommended?: boolean;
  disabled?: boolean;
  /** Default reason to prefill the dropdown when opened */
  defaultReason?: string;
}

export const RejectPopover: React.FC<RejectPopoverProps> = ({
  onReject,
  isRecommended = false,
  disabled = false,
  defaultReason,
}) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) return;

    setIsSubmitting(true);
    try {
      const finalReason = reason === 'Other' && otherReason
        ? `Other: ${otherReason}`
        : reason;
      await onReject(finalReason);
      setOpen(false);
      setReason('');
      setOtherReason('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen && defaultReason) {
      setReason(defaultReason);
    }
    if (!newOpen) {
      setReason('');
      setOtherReason('');
    }
  };

  const isDisabled = !reason || (reason === 'Other' && !otherReason.trim());

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'h-9 px-4 rounded-full text-xs font-bold transition-all flex items-center gap-1.5',
            // Disabled state: still visible but clearly inactive
            disabled && 'bg-zinc-800/60 text-zinc-600 cursor-not-allowed',
            // Active states
            !disabled && isRecommended &&
              'bg-rose-500 text-white hover:bg-rose-600 ring-2 ring-rose-400/30 active:scale-95',
            !disabled && !isRecommended &&
              'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300 active:scale-95',
          )}
        >
          <X className="h-3.5 w-3.5" />
          Reject
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0 bg-zinc-900 border-zinc-800 rounded-2xl overflow-hidden"
        align="end"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-zinc-800">
          <h4 className="text-xs font-black text-white uppercase tracking-wider">
            Reject Intake
          </h4>
        </div>

        <div className="p-3 space-y-3">
          {/* Reason select */}
          <div>
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider block mb-2">
              Reason
            </span>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full h-10 px-3 rounded-xl bg-zinc-800 text-white text-xs font-semibold border-0 focus:outline-none focus:ring-2 focus:ring-rose-500/50 appearance-none cursor-pointer"
            >
              <option value="">Select reason...</option>
              {REJECTION_REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {/* Other reason input */}
          {reason === 'Other' && (
            <div>
              <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider block mb-2">
                Specify
              </span>
              <input
                type="text"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Enter reason..."
                className="w-full h-10 px-3 rounded-xl bg-zinc-800 text-white text-xs font-semibold placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
              />
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={isSubmitting}
              className="flex-1 h-9 rounded-xl bg-zinc-800 text-zinc-400 text-xs font-bold hover:bg-zinc-700 hover:text-zinc-300 transition-all active:scale-[0.98] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isDisabled || isSubmitting}
              className={cn(
                'flex-1 h-9 rounded-xl text-xs font-bold transition-all active:scale-[0.98] flex items-center justify-center gap-1.5',
                !isDisabled
                  ? 'bg-rose-500 text-white hover:bg-rose-600'
                  : 'bg-zinc-800 text-zinc-600 cursor-not-allowed',
                isSubmitting && 'opacity-50',
              )}
            >
              {isSubmitting ? 'Rejecting' : 'Reject'}
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
